import React, { useEffect, useState } from 'react';
import {
  ArrowRight, BarChart3, Briefcase, DollarSign, Hash, Lightbulb,
  LineChart, Package, Smartphone, Wallet,
} from 'lucide-react';
import { api } from '../../../shared/services/api';

const FN_COLOR = {
  'Resume Parsing':            '#58a6ff',
  'Resume Scoring':            '#bc8cff',
  'Interview Answer Scoring':  '#3fb950',
  'Question Generation':       '#ffa657',
  'Job Description Generation':'#d29922',
  'JD Bias Analysis':          '#f87171',
  'Skill Suggestions':         '#94a3b8',
  'Other AI Calls':            '#475569',
};

function fmt$(n) { return `$${Number(n).toFixed(n < 0.01 ? 6 : n < 1 ? 4 : 2)}`; }
function fmtK(n) { return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n); }

export default function UsageCost() {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/usage/stats`).then(r => r.json()),
      fetch(`${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/usage/daily`).then(r => r.json()),
    ]).then(([s, d]) => { setStats(s); setDaily(d); }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  const est = stats?.estimates || {};
  const maxDailyCost = Math.max(...daily.map(d => d.cost_usd), 0.0001);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Usage & Cost Analytics</div>
          <div className="page-subtitle">Real token usage from every Claude API call</div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total API Cost',       value: fmt$(stats?.total_cost_usd || 0),    sub: `${stats?.total_calls || 0} API calls`,             color: 'var(--accent)' },
          { label: 'Total Interviews',      value: stats?.total_interviews || 0,         sub: 'resume parses (proxy)',                            color: 'var(--purple)' },
          { label: 'Cost / Interview',      value: fmt$(est.cost_per_interview_usd || 0), sub: 'parse + score + 10 answers',                     color: 'var(--green)' },
          { label: 'Interviews / $1',       value: est.interviews_per_dollar || '—',     sub: `${est.interviews_per_10_usd || '—'} per $10`,      color: '#d29922' },
          { label: 'Input Tokens',          value: fmtK(stats?.total_input_tokens || 0), sub: fmt$((stats?.total_input_tokens || 0) * 3 / 1e6),  color: '#58a6ff' },
          { label: 'Output Tokens',         value: fmtK(stats?.total_output_tokens || 0),sub: fmt$((stats?.total_output_tokens || 0) * 15 / 1e6),color: '#bc8cff' },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: typeof s.value === 'string' && s.value.length > 6 ? 18 : undefined }}>{s.value}</div>
            <div className="stat-change">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Pricing calculator */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wallet size={15} strokeWidth={2} /> Service Pricing Guide</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>based on your actual Claude costs</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
          {[
            { label: 'Break-even price',  value: fmt$(est.suggested_price_per_interview?.break_even || 0),  sub: '0% margin — just API cost',      color: 'var(--text-muted)' },
            { label: '10× margin',        value: fmt$(est.suggested_price_per_interview?.['10x_margin'] || 0), sub: '90% gross margin',             color: 'var(--yellow)' },
            { label: '20× margin',        value: fmt$(est.suggested_price_per_interview?.['20x_margin'] || 0), sub: '95% gross margin (recommended)', color: 'var(--green)' },
            { label: '50× margin',        value: fmt$(est.suggested_price_per_interview?.['50x_margin'] || 0), sub: '98% gross margin (premium)',   color: 'var(--accent)' },
          ].map(p => (
            <div key={p.label} style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{p.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: p.color, fontFamily: 'var(--font-mono)' }}>{p.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{p.sub}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(37,99,235,0.06)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', border: '1px solid rgba(37,99,235,0.15)' }}>
          <Lightbulb size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong>Recommendation:</strong> At <strong>20× margin</strong> ({fmt$(est.suggested_price_per_interview?.['20x_margin'] || 0)}/interview),
          you keep 95 cents of every dollar. A client paying <strong>$99/month</strong> gets roughly <strong>{Math.round(99 / (est.suggested_price_per_interview?.['20x_margin'] || 1))} interviews</strong> — your API cost is only <strong>{fmt$(99 * (est.cost_per_interview_usd || 0) / (est.suggested_price_per_interview?.['20x_margin'] || 1))}</strong>.
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 20 }}>
        {/* Per-function breakdown */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Cost by Function</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>what each AI step costs you</span>
          </div>
          {!stats?.per_function?.length ? (
            <div className="empty-state" style={{ padding: '20px 0' }}>
              <div className="empty-state-icon"><BarChart3 size={28} strokeWidth={1.6} /></div>
              <div className="empty-state-text">No usage recorded yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {stats.per_function.map(fn => {
                const totalCost = stats.total_cost_usd || 1;
                const pct = Math.round((fn.cost_usd / totalCost) * 100);
                const color = FN_COLOR[fn.label] || 'var(--text-muted)';
                return (
                  <div key={fn.fn}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{fn.label}</span>
                      <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color }}>
                        {fmt$(fn.cost_usd)} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({fn.calls} calls · {fmt$(fn.avg_cost_per_call)} avg)</span>
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--bg-hover)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.5s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Daily spend chart */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Daily Spend (30 days)</div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>API cost per day</span>
          </div>
          {daily.length === 0 ? (
            <div className="empty-state" style={{ padding: '20px 0' }}>
              <div className="empty-state-icon"><LineChart size={28} strokeWidth={1.6} /></div>
              <div className="empty-state-text">No usage data yet</div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, marginBottom: 8 }}>
                {daily.map(d => {
                  const h = Math.max(4, Math.round((d.cost_usd / maxDailyCost) * 110));
                  return (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                      title={`${d.date}\n${fmt$(d.cost_usd)} · ${d.interviews} interviews · ${d.calls} calls`}>
                      <div style={{ width: '100%', height: h, background: 'var(--accent)', borderRadius: '2px 2px 0 0', opacity: 0.8 }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)' }}>
                <span>{daily[0]?.date?.slice(5)}</span>
                <span>{daily[daily.length - 1]?.date?.slice(5)}</span>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                <span>Last 30d total: <strong style={{ color: 'var(--accent)' }}>{fmt$(daily.reduce((s, d) => s + d.cost_usd, 0))}</strong></span>
                <span>Interviews: <strong>{daily.reduce((s, d) => s + d.interviews, 0)}</strong></span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* React Developer specific breakdown */}
      <div className="card">
        <div className="card-header">
          <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Smartphone size={15} strokeWidth={2} /> React Developer Interview — Cost Breakdown</div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>estimated from typical token counts</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Step', 'When', 'Input tokens', 'Output tokens', 'Cost (est.)'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { step: 'Generate Screening Questions', when: 'Job created (once)', in: 520, out: 1500, note: 'shared across all candidates' },
                { step: 'Parse Resume',                 when: 'On apply',          in: 900, out: 500,  note: 'resume text ~3k chars' },
                { step: 'Score Resume',                 when: 'On apply',          in: 680, out: 620,  note: '6 scoring dimensions' },
                { step: 'Score Answer × 8 standard',    when: 'Post-interview',    in: 2400, out: 1200, note: '300 in / 150 out each' },
                { step: 'Score Answer × 6 technical',   when: 'Post-interview',    in: 2100, out: 1050, note: 'React-specific questions' },
                { step: 'TOTAL per candidate',          when: '',                  in: 6100, out: 3370, note: '', bold: true },
              ].map((row, i) => {
                const cost = (row.in * 3 + row.out * 15) / 1_000_000;
                return (
                  <tr key={i} style={{
                    borderBottom: '1px solid var(--border)',
                    background: row.bold ? 'rgba(37,99,235,0.06)' : undefined,
                    fontWeight: row.bold ? 700 : 400,
                  }}>
                    <td style={{ padding: '9px 12px', color: row.bold ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {row.step}
                      {row.note && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>{row.note}</div>}
                    </td>
                    <td style={{ padding: '9px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>{row.when}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'var(--font-mono)', color: '#58a6ff' }}>{fmtK(row.in)}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'var(--font-mono)', color: '#bc8cff' }}>{fmtK(row.out)}</td>
                    <td style={{ padding: '9px 12px', fontFamily: 'var(--font-mono)', color: row.bold ? 'var(--green)' : 'var(--text-secondary)' }}>{fmt$(cost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
          <div style={{ padding: '8px 14px', background: 'rgba(63,185,80,0.1)', borderRadius: 8, border: '1px solid rgba(63,185,80,0.2)' }}>
            <DollarSign size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong style={{ color: 'var(--green)' }}>$0.069</strong> per React Developer interview
          </div>
          <div style={{ padding: '8px 14px', background: 'rgba(99,102,241,0.1)', borderRadius: 8, border: '1px solid rgba(99,102,241,0.2)' }}>
            <Hash size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong style={{ color: 'var(--accent)' }}>~14 interviews per $1</strong>
          </div>
          <div style={{ padding: '8px 14px', background: 'rgba(245,158,11,0.1)', borderRadius: 8, border: '1px solid rgba(245,158,11,0.2)' }}>
            <Package size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong style={{ color: 'var(--yellow)' }}>$3 <ArrowRight size={12} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> 43 interviews</strong>
          </div>
          <div style={{ padding: '8px 14px', background: 'rgba(37,99,235,0.1)', borderRadius: 8, border: '1px solid rgba(37,99,235,0.2)' }}>
            <Briefcase size={13} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> <strong style={{ color: 'var(--accent)' }}>Sell at $1–2/interview <ArrowRight size={12} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> 15–30× ROI</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
